// ¿Le queda lugar en su plan para sumar una marca? — ADR-2026-10-27-planes-de-equipo-en-el-front.
//
// Lo preguntan todas las puertas de "Añadir nueva marca": el selector de la sidebar, el de
// mobile, el del shell, Mis marcas, Información y las pantallas del alta de una marca. Antes
// preguntaban `planAllowsMultiBrand` (cualquier plan pago, sin contar); ahora cuentan contra
// el tope del plan (`topeDeMarcas`).
//
// Es un aviso, no el candado: el candado es el trigger de `brand_profiles`
// (ADR-2026-10-27-tope-de-marcas-en-la-base). Por eso, si la lectura falla, deja seguir: lo
// peor que pasa es que la base frene la marca al guardarla, con su propio mensaje.
//
// Una empresa con facturación por marca no tiene tope: cada marca paga su plan y nace en
// borrador hasta pagarlo (ADR-2026-10-28-facturacion-por-marca-sin-tope-de-marcas). Ahí quién
// puede sumar lo decide `decidirMarcaNueva` (sólo el dueño), no el plan de la empresa.

import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { topeDeMarcas } from './planCapabilities';
import { asegurarSaldoLeido, ultimoSaldoLeido } from './saldo';

export interface LugarParaMarcas {
  plan: string | null;
  /** Marcas (no borrador) que la cuenta ya tiene, de las que esta persona ve. */
  marcas: number;
  /** `null` = sin tope (Agencias, Corporativo), cuenta de pruebas o facturación por marca. */
  tope: number | null;
  puedeSumar: boolean;
}

/**
 * Pura, para poder testearla. `dev_tester` y la facturación por marca pasan siempre, como en la
 * base (`guard_tope_de_marcas`).
 */
export function lugarParaMarcas(p: {
  plan: string | null;
  marcas: number;
  devTester: boolean;
  facturaPorMarca?: boolean;
}): LugarParaMarcas {
  const tope = p.devTester || p.facturaPorMarca === true ? null : topeDeMarcas(p.plan);
  return { plan: p.plan, marcas: p.marcas, tope, puedeSumar: tope === null || p.marcas < tope };
}

/**
 * ¿La empresa factura por marca? Es de la empresa (no cambia con la marca activa), así que
 * alcanza el último saldo leído; si no hay, se lee. Sin saber, como sin bandera: el tope de
 * siempre (y la base, que sí lo sabe, no frena a quien tiene la bandera).
 */
async function facturaPorMarca(): Promise<boolean> {
  const ultimo = ultimoSaldoLeido();
  if (ultimo) return ultimo.facturaPorMarca;
  return (await asegurarSaldoLeido())?.facturaPorMarca === true;
}

/** Cuando no se pudo leer: deja seguir (la base decide). */
const SIN_DATOS: LugarParaMarcas = { plan: null, marcas: 0, tope: null, puedeSumar: true };

export async function leerLugarParaMarcas(): Promise<LugarParaMarcas> {
  try {
    // La sesión local alcanza para saber quién es: `getUser` iría al servidor en cada apertura
    // del selector. Lo que se lee después lo filtra igual la RLS.
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return SIN_DATOS;
    const { data: perfil, error: errorPerfil } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    const idCompany = (perfil as { id_company?: string | null } | null)?.id_company;
    if (errorPerfil || !idCompany) return SIN_DATOS;
    const [empresa, marcas, porMarca] = await Promise.all([
      supabase.from('company_profiles').select('plan, dev_tester').eq('id_company', idCompany).single(),
      supabase
        .from('brand_profiles')
        .select('id', { count: 'exact', head: true })
        .eq('id_company', idCompany)
        .eq('is_draft', false),
      facturaPorMarca().catch(() => false),
    ]);
    if (empresa.error || !empresa.data || marcas.error || marcas.count === null) return SIN_DATOS;
    const e = empresa.data as { plan?: string | null; dev_tester?: boolean | null };
    return lugarParaMarcas({
      plan: e.plan ?? null,
      marcas: marcas.count,
      devTester: e.dev_tester === true,
      facturaPorMarca: porMarca,
    });
  } catch {
    return SIN_DATOS;
  }
}

/**
 * Para pintar el candado y el "2 de 3 marcas" en un selector: se lee cuando `activo` pasa a
 * true (al abrir el menú), cada vez que cambia la marca y cuando cambia `recargar` (por
 * ejemplo, la cantidad de marcas de una lista que acaba de borrar una). `null` mientras no
 * se sabe.
 */
export function useLugarParaMarcas(activo: boolean, recargar?: unknown): LugarParaMarcas | null {
  const [lugar, setLugar] = useState<LugarParaMarcas | null>(null);
  useEffect(() => {
    if (!activo) return;
    let vigente = true;
    const leer = () => {
      void leerLugarParaMarcas().then((l) => {
        if (vigente) setLugar(l);
      });
    };
    leer();
    window.addEventListener('atlas:brand-changed', leer);
    return () => {
      vigente = false;
      window.removeEventListener('atlas:brand-changed', leer);
    };
  }, [activo, recargar]);
  return lugar;
}

/**
 * "2 de 3 marcas" — o `null` si no hay tope que mostrar. Quien tiene más que su tope (las
 * cuentas de antes del tope) no ve un "14 de 1", que parece un error: ve cuántas tiene y
 * cuántas incluye su plan.
 */
export function textoDeMarcasUsadas(lugar: LugarParaMarcas | null): string | null {
  if (!lugar || lugar.tope === null) return null;
  const incluye = `${lugar.tope} ${lugar.tope === 1 ? 'marca' : 'marcas'}`;
  if (lugar.marcas > lugar.tope) return `${lugar.marcas} marcas · tu plan incluye ${incluye}`;
  return `${lugar.marcas} de ${incluye}`;
}
